import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const INITIAL = { reporter_role: "", club: "", first_name: "", last_name: "", age: "", message: "" };

export const Safeguarding = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/safeguarding", {
        ...form,
        age: form.age ? Number(form.age) : null,
      });
      setDone(true);
      toast.success("Segnalazione inviata in modo anonimo.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="safeguarding" data-testid="safeguarding-section" className="py-16 sm:py-24 lg:py-32 bg-[#0A0D13]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-sky-500/10 border border-sky-500/30 text-sky-400">
            <ShieldCheck className="w-3.5 h-3.5" /> Tutela dei minori
          </span>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Safe<span className="text-orange-400">Guarding</span>
          </h2>
          <p className="mt-5 text-base text-slate-300 leading-relaxed">
            La sicurezza e il benessere dei giovani atleti vengono prima di tutto.
            Se hai vissuto o sei a conoscenza di una situazione che riguarda la tutela di un minore
            in ambito sportivo, puoi segnalarla qui: il modulo è completamente anonimo e la
            segnalazione arriva direttamente e solo al Responsabile della Tutela dei Minori
            in materia di SafeGuarding.
          </p>
          <div className="mt-6 flex items-start gap-3 bg-[#10141D] border border-[#222A3B] rounded-2xl p-4">
            <Lock className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-400">
              Non ti chiediamo né nome né contatti: nessun dato del segnalante viene raccolto o salvato.
              Puoi indicare i dati del soggetto leso solo se lo ritieni opportuno. Tieni conto che
              fornire i dati del soggetto che ha bisogno di aiuto, anche solo parziali, permette un
              intervento in aiuto tempestivo e può fare la differenza.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
          {done ? (
            <div className="bg-[#10141D] border border-sky-500/40 rounded-3xl p-10 text-center shadow-[0_0_40px_rgba(56,189,248,0.12)]">
              <CheckCircle2 className="w-14 h-14 text-sky-400 mx-auto" />
              <h3 className="mt-5 text-xl font-bold text-white font-heading">Segnalazione inviata</h3>
              <p data-testid="safeguarding-success-message" className="mt-3 text-sm text-slate-300">
                Grazie per aver parlato. La tua segnalazione è stata consegnata in forma anonima
                e sarà presa in carico con la massima riservatezza.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="safeguarding-form"
              className="bg-[#10141D]/90 backdrop-blur-xl border border-[#222A3B] rounded-3xl p-6 sm:p-8 space-y-5 shadow-2xl">
              <div>
                <Label className="text-slate-300">Soggetto leso *</Label>
                <Select required value={form.reporter_role} onValueChange={set("reporter_role")}>
                  <SelectTrigger data-testid="safeguarding-role-select" className="mt-2 bg-[#08090C] border-[#222A3B] text-white">
                    <SelectValue placeholder="Chi sta facendo la segnalazione?" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#181E2B] border-[#222A3B] text-white">
                    <SelectItem value="soggetto_leso">Sono il soggetto leso</SelectItem>
                    <SelectItem value="terzo_informato">Sono un soggetto terzo informato sui fatti</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sg-club" className="text-slate-300">Società Sportiva interessata *</Label>
                <Input id="sg-club" data-testid="safeguarding-club-input" required maxLength={150}
                  value={form.club} onChange={set("club")} placeholder="Nome della società sportiva"
                  className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                <p className="mt-1 text-xs text-slate-600 text-right">{form.club.length}/150</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                <div>
                  <Label htmlFor="sg-nome" className="text-slate-300">Nome soggetto leso</Label>
                  <Input id="sg-nome" data-testid="safeguarding-firstname-input" maxLength={30}
                    value={form.first_name} onChange={set("first_name")} placeholder="Facoltativo"
                    className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
                <div>
                  <Label htmlFor="sg-cognome" className="text-slate-300">Cognome soggetto leso</Label>
                  <Input id="sg-cognome" data-testid="safeguarding-lastname-input" maxLength={30}
                    value={form.last_name} onChange={set("last_name")} placeholder="Facoltativo"
                    className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
                <div>
                  <Label htmlFor="sg-eta" className="text-slate-300">Età soggetto leso</Label>
                  <Input id="sg-eta" data-testid="safeguarding-age-input" type="number" min="1" max="99"
                    value={form.age} onChange={set("age")} placeholder="Facoltativa"
                    className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
              </div>
              <div>
                <Label htmlFor="sg-msg" className="text-slate-300">Cosa posso fare per te? *</Label>
                <Textarea id="sg-msg" data-testid="safeguarding-message-input" required maxLength={1500}
                  value={form.message} onChange={set("message")}
                  placeholder="Racconta cosa è successo o cosa ti preoccupa. Sei in uno spazio sicuro."
                  className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500 min-h-[140px]" />
                <p className="mt-1 text-xs text-slate-600 text-right">{form.message.length}/1500</p>
              </div>
              <Button type="submit" data-testid="safeguarding-submit-btn" disabled={loading}
                className="w-full bg-sky-400 text-[#050B14] hover:bg-sky-300 font-semibold h-12 text-base shadow-[0_0_25px_rgba(56,189,248,0.25)]">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia segnalazione anonima"}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                Segnalazione anonima: nessun dato personale del segnalante viene raccolto.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
